import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useState,
  useContext,
  useEffect,
} from "react";
import { Vector3 } from "three";
import { Common } from "../../../../common/src";
import { ProjectDetail } from "../../../../common/src/modules";
import {
  EViewerMode,
  ProjectDetailContext,
} from "../../../pages/ProjectDetail";

import {
  CoordinatePointColumn,
  ECoordinateTransformMode,
  RANGE_COORD,
  ReferencePointColumn,
  VerificationPointColumn,
} from "../../config/componentsConfig";
import PointUtil from "../../Utility/PointUtil";
import { projectPointsAPIs } from "../../../api/project";
import { config } from "../../../config/config";
import axios from "axios";
import _ from "lodash";

type HelmContextType = {
  currentHelmTab: ECoordinateTransformMode;
  setCurrentHelmTab: Dispatch<SetStateAction<ECoordinateTransformMode>>;
  coordinatePoints: CoordinatePointColumn[];
  setCoordinatePoints: Dispatch<SetStateAction<CoordinatePointColumn[]>>;
  fetchCoordinatePoints: (projectId: string) => void;
  referencePoints: ReferencePointColumn[];
  setReferencePoints: Dispatch<SetStateAction<ReferencePointColumn[]>>;
  fetchReferencePoints: (
    projectId: string,
    abortControllerSignal?: AbortSignal
  ) => void;
  verificationPoints: VerificationPointColumn[];
  setVerificationPoints: Dispatch<SetStateAction<VerificationPointColumn[]>>;
  fetchVerificationPoints: (
    projectDetail: ProjectDetail,
    abortControllerSignal?: AbortSignal
  ) => void;
  showDeleteAllRowsDialog: boolean;
  setShowDeleteAllRowsDialog: Dispatch<SetStateAction<boolean>>;
  handleDelete: (ctMode: ECoordinateTransformMode, selectedId?: string) => void;
  handleSave: (ctMode: ECoordinateTransformMode, selectedId?: string) => void;
  handleAdd: (ctMode: ECoordinateTransformMode) => void;
  handleUpdate: (
    ctMode: ECoordinateTransformMode,
    pointRowValue: ReferencePointColumn | VerificationPointColumn,
    id: string
  ) => void;
  handlePicking: (
    operation: "start" | "end",
    ctMode: ECoordinateTransformMode
  ) => void;
  handleAllPickPoints: (ctMode: ECoordinateTransformMode) => void;
  autoMapCoordinate: (projectId: string) => Promise<void>;
  verificationSaveFunction: (
    id: string,
    key: string,
    value: string,
    points?: VerificationPointColumn[]
  ) => void;
};

const HelmContext = createContext({
  currentHelmTab: ECoordinateTransformMode.INPUT,
} as HelmContextType);

export default HelmContext;

const defaultPickedPoint = new Vector3(0, 0, 0);

export const HelmContextProvider = (props: { children: ReactNode }) => {
  const {
    currentPointCloud,
    projectDetail,
    getProjectDetail,
    viewer,
    currentViewerMode,
  } = useContext(ProjectDetailContext);
  const [currentHelmTab, setCurrentHelmTab] =
    useState<ECoordinateTransformMode>(ECoordinateTransformMode.INPUT);
  const [coordinatePoints, setCoordinatePoints] = useState<
    CoordinatePointColumn[]
  >([]);
  const [referencePoints, setReferencePoints] = useState<
    ReferencePointColumn[]
  >([]); // Table data
  const [verificationPoints, setVerificationPoints] = useState<
    VerificationPointColumn[]
  >([]); // Table data
  const [showDeleteAllRowsDialog, setShowDeleteAllRowsDialog] =
    useState<boolean>(false);
  const [pickedPoint, setPickedPoint] = useState<Vector3>(defaultPickedPoint);

  useEffect(() => {
    if (currentViewerMode !== EViewerMode.HELMERT_TRANSFORM) {
      viewer.deleteAllPickPoint();
      viewer.endPicking();
    }
  }, [currentViewerMode]);

  const stringCompare = (lhs: string, rhs: string): number => {
    if (lhs < rhs) {
      return -1;
    }
    if (lhs > rhs) {
      return 1;
    }
    return 0;
  };

  // 座標点の取得
  const fetchCoordinatePoints = async (projectId: string) => {
    try {
      const res = await projectPointsAPIs.coordinate.get(projectId);
      const pointGetFromBe = res.data.points;
      // 日付降順にソート
      let sortedPoints = pointGetFromBe.sort((a, b) => {
        if (a.timeStamp !== b.timeStamp) {
          return a.timeStamp > b.timeStamp ? 1 : -1;
        } else {
          return stringCompare(a.name, b.name);
        }
      });
      const records: CoordinatePointColumn[] = [];
      for (const point of sortedPoints) {
        const record: CoordinatePointColumn = {
          id: point.id,
          pointName: point.name,
          x: Math.round(point.x * 1000) / 1000,
          y: Math.round(point.y * 1000) / 1000,
          z: Math.round(point.z * 1000) / 1000,
          timeStamp: point.timeStamp,
        };
        records.push(record);
      }
      setCoordinatePoints(records);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchReferencePoints = async (
    projectId: string,
    abortControllerSignal?: AbortSignal
  ) => {
    try {
      // 座標入力読み込み
      const rescoord = await projectPointsAPIs.coordinate.get(projectId);
      const pointGetFromBe = rescoord.data.points;
      // 日付降順にソート
      let sortedPoints = pointGetFromBe.sort((a, b) => {
        if (a.timeStamp !== b.timeStamp) {
          return a.timeStamp > b.timeStamp ? 1 : -1;
        } else {
          return stringCompare(a.name, b.name);
        }
      });
      const recordsCoord: CoordinatePointColumn[] = [];
      for (const point of sortedPoints) {
        const record: CoordinatePointColumn = {
          id: point.id,
          pointName: point.name,
          x: Math.round(point.x * 1000) / 1000,
          y: Math.round(point.y * 1000) / 1000,
          z: Math.round(point.z * 1000) / 1000,
          timeStamp: point.timeStamp,
        };
        recordsCoord.push(record);
      }
      setCoordinatePoints(recordsCoord);

      const res = await projectPointsAPIs.reference.get(
        projectId,
        abortControllerSignal
      );
      const data = res.data;
      const records: ReferencePointColumn[] = [];
      for (const point of data.points) {
        const correspondingPoint = recordsCoord?.find(
          (correspondPoint) => correspondPoint.id === point.correspondingPointId
        );
        const record: ReferencePointColumn = {
          id: point.id,
          name: point.name,
          correspondingPointId: point.correspondingPointId,
          correspondingX: correspondingPoint?.x ? correspondingPoint.x : 0,
          correspondingY: correspondingPoint?.y ? correspondingPoint.y : 0,
          correspondingZ: correspondingPoint?.z ? correspondingPoint.z : 0,
          x: Math.round(point.x * 1000) / 1000,
          y: Math.round(point.y * 1000) / 1000,
          z: Math.round(point.z * 1000) / 1000,
          dx: Math.round(point.dx * 1000) / 1000,
          dy: Math.round(point.dy * 1000) / 1000,
          dz: Math.round(point.dz * 1000) / 1000,
          use: point.use,
          timeStamp: correspondingPoint?.timeStamp
            ? correspondingPoint.timeStamp
            : new Date(0),
        };
        records.push(record);
      }
      setReferencePoints(records);
      if (currentPointCloud === Common.POINT_CLOUD_TYPE.SFM) {
        resetAllPickPoints(
          records,
          ECoordinateTransformMode.TRANSFORM,
          recordsCoord
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchVerificationPoints = async (
    projectDetail: ProjectDetail,
    abortControllerSignal?: AbortSignal
  ) => {
    try {
      // 座標入力読み込み
      const rescoord = await projectPointsAPIs.coordinate.get(
        projectDetail?.id
      );
      const pointGetFromBe = rescoord.data.points;
      // 日付降順にソート
      let sortedPoints = pointGetFromBe.sort((a, b) => {
        if (a.timeStamp !== b.timeStamp) {
          return a.timeStamp > b.timeStamp ? 1 : -1;
        } else {
          return stringCompare(a.name, b.name);
        }
      });
      const recordsCoord: CoordinatePointColumn[] = [];
      for (const point of sortedPoints) {
        const record: CoordinatePointColumn = {
          id: point.id,
          pointName: point.name,
          x: Math.round(point.x * 1000) / 1000,
          y: Math.round(point.y * 1000) / 1000,
          z: Math.round(point.z * 1000) / 1000,
          timeStamp: point.timeStamp,
        };
        recordsCoord.push(record);
      }
      setCoordinatePoints(recordsCoord);

      const res = await projectPointsAPIs.verification.get(
        projectDetail?.id,
        abortControllerSignal
      );
      const data = res.data;
      const records: VerificationPointColumn[] = [];
      for (const point of data.points) {
        const correspondingPoint = recordsCoord?.find(
          (correspondPoint) => correspondPoint.id === point.correspondingPointId
        );
        const record: VerificationPointColumn = {
          id: point.id,
          name: point.name,
          correspondingPointId: point.correspondingPointId,
          x: Math.round(point.x * 1000) / 1000,
          y: Math.round(point.y * 1000) / 1000,
          z: Math.round(point.z * 1000) / 1000,
          trueX: correspondingPoint?.x ? correspondingPoint.x : 0,
          trueY: correspondingPoint?.y ? correspondingPoint.y : 0,
          trueZ: correspondingPoint?.z ? correspondingPoint.z : 0,
          dx: Math.round(point.dx * 1000) / 1000,
          dy: Math.round(point.dy * 1000) / 1000,
          dz: Math.round(point.dz * 1000) / 1000,
          use: point.use,
          timeStamp: correspondingPoint?.timeStamp
            ? correspondingPoint.timeStamp
            : new Date(0),
        };
        records.push(record);
      }
      setVerificationPoints(records);
      if (
        currentPointCloud === Common.POINT_CLOUD_TYPE.HELMERT ||
        (currentPointCloud === Common.POINT_CLOUD_TYPE.SFM &&
          projectDetail.useViDoc)
      ) {
        resetAllPickPoints(
          records,
          ECoordinateTransformMode.VERIFICATION,
          recordsCoord
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const resetAllPickPoints = (
    records: VerificationPointColumn[] | ReferencePointColumn[],
    type: ECoordinateTransformMode,
    recordsCoord?: CoordinatePointColumn[]
  ) => {
    viewer.deleteAllPickPoint();
    records.map((record) => {
      const correspondingPoint = (
        recordsCoord ? recordsCoord : coordinatePoints
      ).find(
        (correspondPoint) => correspondPoint.id === record.correspondingPointId
      );
      const correspoidingName = correspondingPoint
        ? correspondingPoint.pointName
        : "";
      addPickPoint(
        record.id,
        correspoidingName,
        currentPointCloud === Common.POINT_CLOUD_TYPE.SFM &&
          type === ECoordinateTransformMode.TRANSFORM
          ? new Vector3(record.x, record.y, record.z)
          : new Vector3(record.y, record.x, record.z)
      );
    });
  };

  const addPickPoint = (id: string, name: string, position: Vector3) => {
    const offset = viewer.getOffset();
    viewer.addPickPoint(id, name, position.add(offset));
  };

  // 検証点精度の計算
  const verification = (id?: string, points?: VerificationPointColumn[]) => {
    // ２重更新制限
    // setIsInVerification(true);
    // setIsVerificationFetching(true);

    // Para作成
    const updateDataList = [] as Common.PointVerificationForUpdate[];
    (points ? points : verificationPoints).map((r: VerificationPointColumn) => {
      if (r.correspondingPointId !== null && r.id === id) {
        r.dx = r.trueX - r.x;
        r.dy = r.trueY - r.y;
        r.dz = r.trueZ - r.z;
        const updateData: Common.PointVerificationForUpdate = {
          id: r.id,
          dx: r.dx,
          dy: r.dy,
          dz: r.dz,
        };
        updateDataList.push(updateData);
      }
      return r;
    });

    // statusの更新フラグ設定
    let updateFlag: boolean = false;
    if (
      projectDetail.status.helmert_transformation ===
        Common.PROJECT_STATUS_ENUM.COMPLETED ||
      projectDetail.status.coordinate_transformation ===
        Common.PROJECT_STATUS_ENUM.COMPLETED ||
      projectDetail.status.verification_helmert ===
        Common.PROJECT_STATUS_ENUM.COMPLETED ||
      projectDetail.status.sfm_processing ===
        Common.PROJECT_STATUS_ENUM.COMPLETED ||
      projectDetail?.status.verification_sfm ===
        Common.PROJECT_STATUS_ENUM.COMPLETED
    ) {
      updateFlag = true;
    }

    const body = {
      data: updateDataList,
      updateStatus: updateFlag,
      // snapshot: viewer.getCanvas().toDataURL("image/jpeg"),
    } as Common.VerifiedPointUpdateBody;

    // API呼び出しDB更新
    axios
      .put(
        `${config.serverHost}/api/v1/project/${projectDetail?.id}/points/verified`,
        body,
        { withCredentials: true }
      )
      .then(() => {
        // setIsInVerification(false);
        // pointProps.setInitDone(false); // 画面自動リロードさせる
      })
      .catch(() => {
        // setIsInVerification(false);
      })
      .finally(() => {
        getProjectDetail(false);
        // setIsVerificationFetching(false);
      });
  };

  const verificationSaveFunction = async (
    id: string,
    key: string,
    value: string,
    points?: VerificationPointColumn[]
  ) => {
    try {
      if (value !== "" && id !== "" && key !== "") {
        await PointUtil.PointVerificationUpdate(
          id,
          key,
          value,
          projectDetail?.id,
          points ? points : verificationPoints
        );
        verification(id, points);
      }
    } catch (err) {
      console.error("Error Msg:", err);
    }
  };

  const debouncedSave = _.debounce(verificationSaveFunction, 1000);

  const autoMapCoordinate = async (projectId: string) => {
    const dataInRangeArr: { id: string; key: string; value: string }[] = [];
    var newData = await Promise.all(
      verificationPoints.map(async (item) => {
        let dataInRange = null as CoordinatePointColumn;
        for (var i = 0; i < coordinatePoints?.length; i++) {
          let coordInput = coordinatePoints[i];
          if (checkDataInRange(item, coordInput)) {
            dataInRange = coordInput;
            break;
          }
        }
        if (dataInRange && item.correspondingPointId !== dataInRange.id) {
          const pointUpdateReference: Common.PointVerificationForUpdate = {
            id: item.id,
            correspondingPointId: dataInRange.id,
          };
          await projectPointsAPIs.verification.put(
            projectId,
            pointUpdateReference
          );
          updatePickPoint(
            pointUpdateReference.id,
            dataInRange.pointName,
            undefined
          );
          dataInRangeArr.push({
            id: item.id,
            key: "correspondingPointId",
            value: dataInRange.id,
          });
          return {
            ...item,
            correspondingPointId: dataInRange.id,
            trueX: dataInRange.x,
            trueY: dataInRange.y,
            trueZ: dataInRange.z,
            timeStamp: dataInRange.timeStamp,
            dx: dataInRange.x - item.x,
            dy: dataInRange.y - item.y,
            dz: dataInRange.z - item.z,
          };
        } else {
          return item;
        }
      })
    );
    setVerificationPoints(newData);
    for (const data of dataInRangeArr) {
      if (data.id !== "new") {
        debouncedSave(data.id, data.key, data.value, newData);
      }
    }
    if (dataInRangeArr.length === 0) {
      verification(null, newData);
    }
  };

  const updatePickPoint = (id: string, name: string, position: Vector3) => {
    const offset = viewer.getOffset();
    viewer.updatePickPoint(
      id,
      name,
      position ? position.add(offset) : undefined
    );
  };

  // checkData in range
  const checkDataInRange = (
    itemFirst: VerificationPointColumn,
    itemSecond: CoordinatePointColumn
  ) => {
    if (
      itemFirst.x > itemSecond.x + RANGE_COORD ||
      itemFirst.x < itemSecond.x - RANGE_COORD
    )
      return false;
    if (
      itemFirst.y > itemSecond.y + RANGE_COORD ||
      itemFirst.y < itemSecond.y - RANGE_COORD
    )
      return false;
    if (
      itemFirst.z > itemSecond.z + RANGE_COORD ||
      itemFirst.z < itemSecond.z - RANGE_COORD
    )
      return false;
    return true;
  };

  const handleDelete = (
    ctMode: ECoordinateTransformMode,
    selectedId?: string
  ) => {
    if (ctMode === ECoordinateTransformMode.INPUT)
      return deleteCoordinatePoints(selectedId);
    else if (ctMode === ECoordinateTransformMode.TRANSFORM)
      return deleteReferencePoints(selectedId);
    else if (ctMode === ECoordinateTransformMode.VERIFICATION)
      return deleteVerificationPoints(selectedId);
  };

  const deleteCoordinatePoints = async (selectedID?: string) => {
    // setIsCoordinateFetching(true);
    if (selectedID) {
      // 選択削除
      await PointUtil.pointRowDel([selectedID], projectDetail?.id);
    } else {
      // 全削除
      await PointUtil.pointRowDel(
        coordinatePoints.map((n) => n.id),
        projectDetail?.id
      );
    }
    // setIsCoordinateFetching(false);
    fetchCoordinatePoints(projectDetail?.id);
  };

  const deleteReferencePoints = async (selectedID?: string) => {
    try {
      if (referencePoints.length === 0) return;
      // setIsTransformFetching(true);
      const deleteItemIds: string[] = [];

      if (selectedID) {
        // 選択削除
        deleteItemIds.push(selectedID);
      } else {
        // 全削除
        referencePoints.map((n) => deleteItemIds.push(n.id));
      }
      const body: Common.PointCoordinateDeleteInput = {
        ids: deleteItemIds.filter((id) => id !== "new"),
      };
      await projectPointsAPIs.reference.delete(projectDetail?.id, body);
      deleteItemIds.forEach((id) => {
        viewer.deletePickPoint(id);
      });
      fetchReferencePoints(projectDetail?.id);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteVerificationPoints = async (selectedID?: string) => {
    try {
      if (verificationPoints.length === 0) {
        return;
      }
      const deleteItemIds: string[] = [];
      if (selectedID) {
        // 選択削除
        deleteItemIds.push(selectedID);
      } else {
        // 全削除
        verificationPoints.map((n) => deleteItemIds.push(n.id));
      }
      const body: Common.PointsVerificationDeleteInput = {
        ids: deleteItemIds.filter((id) => id !== "new"),
      };
      await projectPointsAPIs.verification.delete(projectDetail?.id, body);
      deleteItemIds.forEach((id) => {
        viewer.deletePickPoint(id);
      });
      fetchVerificationPoints(projectDetail);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAdd = (ctMode: ECoordinateTransformMode) => {
    switch (ctMode) {
      case ECoordinateTransformMode.INPUT:
        addCoordinatePoint();
        break;
      case ECoordinateTransformMode.TRANSFORM:
        addReferencePoint();
        break;
      case ECoordinateTransformMode.VERIFICATION:
        addVerificationPoint();
        break;
    }
  };

  const addCoordinatePoint = async () => {
    try {
      const res = await projectPointsAPIs.coordinate.post(
        projectDetail?.id,
        {}
      );
      const data = res.data[0];
      const newRecord: CoordinatePointColumn = {
        id: data.id,
        pointName: data.name,
        x: data.x,
        y: data.y,
        z: data.z,
        timeStamp: data.timeStamp,
      };
      setCoordinatePoints([...coordinatePoints, newRecord]);
    } catch (e) {
      console.log(e);
    }
  };

  const addReferencePoint = async () => {
    try {
      const newRecord: ReferencePointColumn = {
        id: "new",
        name: "empty",
        correspondingX: 0,
        correspondingY: 0,
        correspondingZ: 0,
        x: 0,
        y: 0,
        z: 0,
        dx: 0,
        dy: 0,
        dz: 0,
        use: false,
        timeStamp: new Date(0),
      };
      const _ = [...referencePoints, newRecord];
      setReferencePoints(_);
    } catch (e) {
      console.log(e);
    }
  };

  const addVerificationPoint = async () => {
    try {
      const newRecord: VerificationPointColumn = {
        id: "new",
        name: "empty",
        correspondingPointId: "",
        trueX: 0,
        trueY: 0,
        trueZ: 0,
        x: 0,
        y: 0,
        z: 0,
        dx: 0,
        dy: 0,
        dz: 0,
        use: false,
        timeStamp: new Date(0),
      };
      setVerificationPoints([...verificationPoints, newRecord]);
    } catch (e) {
      console.log(e);
    }
  };

  const handleSave = async (
    ctMode: ECoordinateTransformMode,
    selectedId?: string
  ) => {
    if (ctMode === ECoordinateTransformMode.TRANSFORM)
      await saveReferencePoint(selectedId);
    else if (ctMode === ECoordinateTransformMode.VERIFICATION)
      await saveVerificationPoint(selectedId);
  };

  const saveVerificationPoint = async (id: string) => {
    if (pickedPoint.equals(defaultPickedPoint)) {
      // 点群に点が選択されていない状態 ⇒ 処理しない
      return;
    }
    let newId = id;
    let savePoint = null;
    if (newId === "new") {
      const res = await projectPointsAPIs.verification.post(
        projectDetail?.id,
        {}
      );
      const newPoint = res.data;
      if (!newPoint) {
        return;
      }
      newId = newPoint.id;
      savePoint = verificationPoints.find((point) => point.id === "new");
    }

    // 2.ピックしたデータ取得
    const updateInput: Common.PointVerificationForUpdate = {
      id: newId,
      name: "",
      correspondingPointId: savePoint.correspondingPointId,
      x: savePoint ? savePoint.x : pickedPoint.y,
      y: savePoint ? savePoint.y : pickedPoint.x,
      z: savePoint ? savePoint.z : pickedPoint.z,
      dx: savePoint.dx,
      dy: savePoint.dy,
      dz: savePoint.dz,
    };

    // 3.更新
    const res = await projectPointsAPIs.verification.put(
      projectDetail?.id,
      updateInput
    );
    const updatedPoint = res.data;
    if (!updatedPoint) {
      return;
    }
    setVerificationPoints(
      verificationPoints.map((prevRow) =>
        prevRow.id === "new"
          ? Object.assign(prevRow, {
              id: newId,
              correspondingPointId: updatedPoint.correspondingPointId,
              name: updatedPoint.name,
              x: updatedPoint.x,
              y: updatedPoint.y,
              z: updatedPoint.z,
            })
          : prevRow
      )
    );
    setPickedPoint(defaultPickedPoint);
    addPickPoint(
      updatedPoint.id,
      "",
      new Vector3(updatedPoint.y, updatedPoint.x, updatedPoint.z)
    );
    verification(newId);
  };

  const saveReferencePoint = async (id: string) => {
    if (pickedPoint.equals(defaultPickedPoint)) {
      // 点群に点が選択されていない状態 ⇒ 処理しない
      return;
    }
    let newId = id;
    let savePoint = null;
    if (newId === "new") {
      const res = await projectPointsAPIs.reference.post(projectDetail?.id, {});
      const newPoint = res.data;
      if (!newPoint) {
        return;
      }
      newId = newPoint.id;
      savePoint = referencePoints.find((point) => point.id === "new");
    }
    console.log(savePoint);
    // 2.ピックしたデータ取得
    const updateInput: Common.PointReferenceForUpdate = {
      id: newId,
      name: "",
      correspondingPointId: savePoint?.correspondingPointId,
      x: savePoint ? savePoint.x : pickedPoint.y,
      y: savePoint ? savePoint.y : pickedPoint.x,
      z: savePoint ? savePoint.z : pickedPoint.z,
    };

    // 3.更新
    const res = await projectPointsAPIs.reference.put(
      projectDetail?.id,
      updateInput
    );
    const updatedPoint = res.data;
    if (!updatedPoint) {
      return;
    }
    const _ = referencePoints.map((prevRow) =>
      prevRow.id === "new"
        ? Object.assign(prevRow, {
            id: newId,
            correspondingPointId: updatedPoint.correspondingPointId,
            name: updatedPoint.name,
            x: updatedPoint.x,
            y: updatedPoint.y,
            z: updatedPoint.z,
          })
        : prevRow
    );
    setReferencePoints(_);
    setPickedPoint(defaultPickedPoint);
    addPickPoint(
      updatedPoint.id,
      "",
      new Vector3(updatedPoint.x, updatedPoint.y, updatedPoint.z)
    );
  };

  const updateReference = async (
    pointRowValue: ReferencePointColumn,
    id: string
  ) => {
    // データのアップデート関数を返却
    try {
      if (!pointRowValue) return;

      const column = coordinatePoints.find((c) => c.id === id);

      const pointUpdateReference: Common.PointReferenceForUpdate = {
        id: pointRowValue.id,
        correspondingPointId: column.id,
      };

      if (pointUpdateReference === undefined) return;

      await projectPointsAPIs.reference.put(
        projectDetail?.id,
        pointUpdateReference
      );

      updatePickPoint(
        pointRowValue.id,
        column.pointName,
        new Vector3(pointRowValue.x, pointRowValue.y, pointRowValue.z)
      );

      // カラムのアップデート
      setReferencePoints(
        referencePoints.map((prevRow) =>
          prevRow.id === pointRowValue.id
            ? Object.assign(prevRow, {
                correspondingPointId: column.id,
                correspondingX: column.x,
                correspondingY: column.y,
                correspondingZ: column.z,
                timeStamp: column.timeStamp,
              })
            : prevRow
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  const updateVerification = async (
    pointRowValue: VerificationPointColumn,
    id: string
  ) => {
    if (!pointRowValue) return;

    const column = coordinatePoints.find((c) => c.id === id);

    const updateVerification: Common.PointVerificationForUpdate = {
      id: pointRowValue.id,
      correspondingPointId: column.id,
    };

    if (updateVerification === undefined) return;

    await projectPointsAPIs.verification.put(
      projectDetail?.id,
      updateVerification
    );
    updatePickPoint(
      pointRowValue.id,
      column.pointName,
      new Vector3(pointRowValue.y, pointRowValue.x, pointRowValue.z)
    );

    setReferencePoints(
      referencePoints.map((prevRow) =>
        prevRow.id === pointRowValue.id
          ? Object.assign(prevRow, {
              correspondingPointId: column.id,
              trueX: column.x,
              trueY: column.y,
              trueZ: column.z,
              timeStamp: column.timeStamp,
            })
          : prevRow
      )
    );
  };

  const handleUpdate = async (
    ctMode: ECoordinateTransformMode,
    pointRowValue: ReferencePointColumn | VerificationPointColumn,
    id: string
  ) => {
    if (ctMode === ECoordinateTransformMode.TRANSFORM)
      return updateReference(pointRowValue as ReferencePointColumn, id);
    else if (ctMode === ECoordinateTransformMode.VERIFICATION)
      return updateVerification(pointRowValue as VerificationPointColumn, id);
  };

  const handlePicking = (
    operation: "start" | "end",
    ctMode: ECoordinateTransformMode
  ) => {
    switch (operation) {
      case "start":
        viewer.startPicking({
          onClick: (resp: { point: Vector3 }) => {
            const offset = viewer.getOffset();
            const newPoint = new Vector3(
              resp.point.x - offset.x,
              resp.point.y - offset.y,
              resp.point.z - offset.z
            );
            setPickedPoint(newPoint);
            updateBottomCoordinate(newPoint, ctMode);
          },
        });
        break;
      case "end":
        viewer.endPicking();
        break;
    }
  };

  const updateBottomCoordinate = (
    newPoint: Vector3,
    ctMode: ECoordinateTransformMode
  ) => {
    if (newPoint.equals(defaultPickedPoint)) {
      // 点群に点が選択されていない状態 ⇒ 処理しない
      return;
    }

    if (ctMode === ECoordinateTransformMode.TRANSFORM) {
      // deep copy
      const _ = referencePoints.map((item) => ({ ...item }));

      _[_.length - 1].x = newPoint.x;
      _[_.length - 1].y = newPoint.y;
      _[_.length - 1].z = newPoint.z;

      // referencePointsRef.current = _;
      setReferencePoints(_);
    } else if (ctMode === ECoordinateTransformMode.VERIFICATION) {
      // deep copy
      const _ = verificationPoints.map((item) => ({ ...item }));

      _[_.length - 1] = Object.assign(_[_.length - 1], {
        x: newPoint.y,
        y: newPoint.x,
        z: newPoint.z,
      });

      setVerificationPoints(_);
    }
  };

  const handleAllPickPoints = (ctMode: ECoordinateTransformMode) => {
    switch (ctMode) {
      case ECoordinateTransformMode.TRANSFORM:
        resetAllPickPoints(referencePoints, ECoordinateTransformMode.TRANSFORM);
        break;
      case ECoordinateTransformMode.VERIFICATION:
        resetAllPickPoints(
          verificationPoints,
          ECoordinateTransformMode.VERIFICATION
        );
        break;
    }
  };

  const contextValue = {
    currentHelmTab,
    setCurrentHelmTab,
    coordinatePoints,
    setCoordinatePoints,
    fetchCoordinatePoints,
    referencePoints,
    setReferencePoints,
    fetchReferencePoints,
    verificationPoints,
    setVerificationPoints,
    fetchVerificationPoints,
    showDeleteAllRowsDialog,
    setShowDeleteAllRowsDialog,
    handleDelete,
    handleAdd,
    handleSave,
    handleUpdate,
    handlePicking,
    handleAllPickPoints,
    autoMapCoordinate,
    verificationSaveFunction,
  };

  return (
    <HelmContext.Provider value={contextValue}>
      {props.children}
    </HelmContext.Provider>
  );
};
